import React, { useCallback } from 'react';
import { cloneDeep } from 'lodash';
import { Drawer } from '@kufox/mui';
import { styled } from '@kufox/mui/emotion';
import useMediaQuery from '@kufox/mui/hooks/useMediaQuery';
import useTheme from '@kufox/mui/hooks/useTheme';
import { useSelector } from 'react-redux';
import { CloseOutlined } from '@kufox/icons';
import NavUser from '../NavUser';
import NavLink from '../Nav/Nav';
import Link from '../../components/Link';
import UserBox from '../UserBox';
import { useLang } from '../../hookTool';
import { getHelpLang, genNewbieZoneItem } from '../Nav/const';
import { namespace } from '../model';
import Point from '../../../static/newHeader/point.svg';
import {
  kcsensorsMenuClick as kcsensorsClick,
  addSpmIntoQuery,
  getMenuSpm,
} from '../../common/tools';

const NavActiveLink = styled(Link)`
  background: ${(props) => props.theme.colors.base};
  width: 100%;
  padding: 16px 24px 16px;
  text-decoration: none;
  position: relative;
  & img {
    content: '';
    width: 58px;
    height: 10px;
    position: absolute;
    display: none;
    top: 9px;
    right: -5px;
  }
  &:hover {
    border-color: #2dbd96;
    & img {
      display: block;
    }
  }
`;

const CloseDiv = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DrawerWrapper = styled.div`
  width: 100%;
  display: flex;
  height: 100vh;
  flex-direction: column;
  justify-content: space-between;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  & .navLinks {
    & .rc-collapse {
      background: ${(props) => props.theme.colors.base} !important;
    }
  }
`;

const NavActiveBtn = styled.div`
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  background: rgba(45, 189, 150, 0.12);
  border-radius: 8px;
  border: 1px solid transparent;
  color: #2dbd96;
  font-size: 14px;
  text-decoration: none !important;
  font-family: URWDIN-Medium;
  white-space: nowrap;
  overflow: hidden;
  &:hover {
    border-color: #2dbd96;
    color: #2dbd96;
    & img {
      display: block;
    }
  }
`;

const MarqueeWrapper = styled.div`
  width: 254px;
  white-space: nowrap;
  overflow: hidden;
  box-sizing: border-box;
  @keyframes marquee {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(-100%, 0);
    }
  }
  & p {
    display: inline-block;
    padding-left: 100%;
    animation: marquee 10s linear infinite;
  }
`;

const CusDrawer = styled(Drawer)`
  width: ${(props) => (props.isMobile ? '100vw' : '320px')};
  height: 100vh;
  overflow: scroll;
  padding: 0 !important;
  background-color: ${(props) => props.colors.base} !important;
  & .showDownBox {
    padding: 0 24px;
  }
  & .userBox {
    margin: 0;
  }
`;

const Hr = styled.hr`
  height: 1px;
  background: ${(props) => props.colors.divider};
  margin: 16px 24px 16px;
  border: none;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  height: 48px;
  line-height: 48px;
  cursor: pointer;
  color: ${(props) => props.colors.text};
  font-family: URWDIN-Medium;
  padding: 0 24px;
  &:hover {
    background: rgba(115, 126, 141, 0.08);
  }
`;

const CusLink = styled(Link)`
  width: 100%;
  height: 100%;
  font-size: 14px;
  color: ${(props) => props.colors.text};
  text-decoration: none;
  &:hover {
    color: ${(props) => props.colors.text};
  }
`;

const ItemValue = styled.div`
  font-family: URWDIN-Regular;
  color: ${(props) => props.colors.text60};
`;

export default function MenuDrawer(props) {
  const {
    show,
    onClose,
    handleShowDrawer,
    currentLang,
    isNav,
    userInfo,
    navStatus,
    hostConfig,
  } = props;
  const { KUCOIN_HOST } = hostConfig || {};
  const isMobile = useMediaQuery('(max-width:400px)');
  const theme = useTheme();
  const { t } = useLang();
  const { langListMap, navActive, navList: _list, currencyList, showNewbieNav } = useSelector(
    (state) => state[namespace],
  );
  const navList = cloneDeep(_list);
  if (navList && navList.length && showNewbieNav)
    navList.push(genNewbieZoneItem(t, currentLang, KUCOIN_HOST));

  // 判断当前url是.cc域名还是.com
  const getUrl = useCallback(
    (navItem) => {
      let jumpUrl = '';
      const { hostname } = window.location;
      // 针对帮助中心语言参数做特殊处理
      if (navItem.uri === 'https://support.kucoin.plus/hc') {
        jumpUrl = `${navItem.uri}/${getHelpLang[currentLang]}`;
      } else if (hostname.includes('.com')) {
        jumpUrl = `${navItem.uri}`;
      } else {
        jumpUrl = `${navItem.zhUri}`;
      }
      return jumpUrl;
    },
    [currentLang],
  );

  const NavActiveComponent = (index) => {
    const navActiveItem =
      navActive.children && navActive.children.length > 0 ? navActive.children[0] : null;
    if (navActiveItem) {
      const _index = index + 1;
      const menuSpm = getMenuSpm(index, 0);
      const menuUrl = addSpmIntoQuery(getUrl(navActiveItem), menuSpm);
      const activeTag = document.getElementById('activeSpan'); // 找到元素
      const isMarquee = !!(activeTag && activeTag.offsetWidth >= 16 * 254);
      return (
        <NavActiveLink
          theme={theme}
          href={menuUrl}
          onClick={
            navActiveItem.onClick ||
            function() {
              kcsensorsClick(menuSpm, { url: menuUrl });
            }
          }
          data-ga={navActiveItem.id}
          data-modid={`headers${_index}`}
          lang={currentLang}
        >
          <NavActiveBtn>
            <div id="activeSpan">
              {isMarquee ? (
                <MarqueeWrapper>
                  <p>{navActiveItem.name}</p>
                </MarqueeWrapper>
              ) : (
                navActiveItem.name
              )}
            </div>
          </NavActiveBtn>
          <img src={Point} alt="" />
        </NavActiveLink>
      );
    }
    return null;
  };
  return (
    <CusDrawer
      anchor="right"
      show={show}
      onClose={onClose}
      isMobile={isMobile}
      colors={theme.colors}
    >
      <DrawerWrapper>
        <CloseDiv>
          <CloseOutlined
            size="24"
            onClick={onClose}
            style={{ cursor: 'pointer', margin: `24px 24px 20px 0` }}
            color={theme.colors.text}
          />
        </CloseDiv>
        <ContentWrapper theme={theme}>
          {!isNav && (
            <div className="userBox">
              <UserBox {...props} inDrawer />
            </div>
          )}
          {isNav && !userInfo && (
            <div className="userBox2">
              <NavUser {...props} inDrawer navStatus={navStatus} />
              <Hr colors={theme.colors} />
            </div>
          )}
          {navStatus === 4 && isNav && (
            <div className="navLinks">
              <NavLink {...props} inDrawer />
              <Hr colors={theme.colors} />
            </div>
          )}
          {navStatus !== 1 && isNav && (
            <div className="downBox">
              <Item colors={theme.colors}>
                <CusLink href="/download" colors={theme.colors}>
                  {t('download')}
                </CusLink>
              </Item>
              <Item colors={theme.colors} onClick={() => handleShowDrawer('i18n', true, 'lang')}>
                {t('language')}{' '}
                <ItemValue colors={theme.colors}>
                  {(langListMap[currentLang || 'en_US'] || {}).langName}
                </ItemValue>
              </Item>
              {!currencyList || !currencyList.length ? null : (
                <Item
                  colors={theme.colors}
                  onClick={() => handleShowDrawer('i18n', true, 'currency')}
                >
                  {t('currency')}{' '}
                  <ItemValue colors={theme.colors}>{props.currency || 'USD'}</ItemValue>
                </Item>
              )}
            </div>
          )}
        </ContentWrapper>
        {isNav && NavActiveComponent(navList.length + 1)}
      </DrawerWrapper>
    </CusDrawer>
  );
}
